import { HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";

import { Observable, mergeMap, retryWhen, scan, timer, throwError } from "rxjs";

import { ErrorResponse } from "../model/error.response";
import { RequestOptions } from "../model/options.request";
import { ApiResponse } from "../model/api.response";

export class TemplateHelper {

    static buildOptions(options?: RequestOptions) {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ...(options?.headers ?? {}),
        });

        let params = new HttpParams();
        if (options?.params) {
            Object.entries(options.params)
                .filter(([, v]) => v !== undefined && v !== null)
                .forEach(([k, v]) => (params = params.set(k, String(v))));
        }

        return { headers, params };
    }

    static applyRetry(options?: RequestOptions) {
        const attempts = options?.retry?.attempts ?? 3;
        const baseDelay = options?.retry?.baseDelayMs ?? 1000;
        const maxDelay = options?.retry?.maxDelayMs ?? 10_000;
        const jitter = options?.retry?.jitter ?? true;

        const defaultRetryOn = (err: unknown) => {
            const e = err as HttpErrorResponse;
            const status = e?.status ?? 0;
            return status === 0 || (status >= 500 && status < 600);
        };
        const retryOn = options?.retry?.retryOn ?? defaultRetryOn;

        if (attempts <= 0) {
            return <U>(source: Observable<U>) => source;
        }

        return <U>(source: Observable<U>) =>
            source.pipe(
                retryWhen((errors) =>
                    errors.pipe(
                        scan((retryCount, error) => {
                            if (!retryOn(error) || retryCount >= attempts) {
                                throw error;
                            }

                            return retryCount + 1;
                        }, 0),
                        mergeMap((retryCount) => {
                            const exp = Math.min(baseDelay * Math.pow(2, retryCount - 1), maxDelay);
                            const withJitter = jitter ? Math.floor(exp * (0.5 + Math.random() * 0.5)) : exp;
                            return timer(withJitter);
                        }),
                    ),
                ),
            );
    }

    static handleError(httpError: HttpErrorResponse) {
        if (typeof httpError.error !== 'string'
            && !(httpError.error instanceof Blob)
            && httpError.error.status) {
            return throwError(() => (httpError.error as ApiResponse<ErrorResponse>));
        }

        const apiError: ApiResponse<ErrorResponse> = {
            status: httpError.status,
            error: true,
            body: TemplateHelper.normalizeHttpError(httpError.error)
        }

        return throwError(() => apiError);
    }

    private static normalizeHttpError(errorBody: unknown): ErrorResponse {
        if (typeof errorBody === 'string') {
            return {
                message: errorBody,
                client: errorBody
            };
        }

        if (errorBody instanceof Blob) {
            return {
                message: 'Unexpected binary error',
                client: errorBody
            };
        }

        return {
            message: 'Unexpected error',
            client: errorBody
        };
    }
}
